<script setup>
import { ref, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useI18n } from 'vue-i18n';

import { messageStamp, dynamicTime } from 'shared/helpers/timeHelper';

import Pagination from 'dashboard/components/table/Pagination.vue';
import UserAvatarWithName from 'dashboard/components/widgets/UserAvatarWithName.vue';
import ShowMore from 'dashboard/components/widgets/ShowMore.vue';
import CsatContactCell from './CsatContactCell.vue';
import CsatExpandedRow from './CsatExpandedRow.vue';
import CsatEmptyState from './CsatEmptyState.vue';
import CsatTableLoader from './CsatTableLoader.vue';

import { CSAT_RATINGS } from 'shared/constants/messages';

import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';

const { pageIndex } = defineProps({
  pageIndex: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['pageChange']);
const { t } = useI18n();
const { isCloudFeatureEnabled, isOnChatwootCloud } = useAccount();
const csatResponses = useMapGetter('csat/getCSATResponses');

const isFeatureEnabled = computed(() =>
  isCloudFeatureEnabled('csat_review_notes')
);
const showExpandableRows = computed(
  () => isFeatureEnabled.value || isOnChatwootCloud.value
);
const metrics = useMapGetter('csat/getMetrics');
const uiFlags = useMapGetter('csat/getUIFlags');

const isLoading = computed(() => uiFlags.value.isFetching);

const expandedRows = ref({});

const toggleRow = id => {
  expandedRows.value = {
    ...expandedRows.value,
    [id]: !expandedRows.value[id],
  };
};

const isRowExpanded = id => !!expandedRows.value[id];

// T-256: UMA LINHA POR ATENDIMENTO, UMA COLUNA POR PERGUNTA.
//
// Desde 18/09 um mesmo atendimento pode receber duas perguntas de nota com sentidos diferentes
// (a qualidade do atendimento e a resolução do problema). Uma linha por resposta fazia a lista
// mostrar o mesmo torcedor duas vezes, com notas diferentes e nada dizendo qual era qual. Agora
// as respostas da mesma conversa se juntam numa linha e cada pergunta tem sua coluna.
//
// A ordem é a do documento de fluxos do clube, não alfabética.
const ORDEM_CRITERIOS = ['qualidade', 'resolucao', 'virtual'];

// ⚠️ O AGRUPAMENTO É DENTRO DA PÁGINA. A paginação do servidor conta RESPOSTAS (25 por página),
// e as duas respostas de um atendimento nascem com segundos de diferença, então caem sempre
// juntas — exceto quando a quebra da página passa exatamente entre elas. Nesse caso o
// atendimento aparece no fim de uma página com uma nota e no início da outra com a outra. Cada
// linha continua certa; só não estão juntas. Juntar de verdade exigiria paginar por conversa no
// controller do upstream, que serve o produto inteiro.
const tableData = computed(() => {
  const porConversa = new Map();

  csatResponses.value.forEach(response => {
    // Resposta sem conversa (dado antigo) vira linha própria, em vez de virar uma chave só com
    // todas elas empilhadas juntas.
    const chave = response.conversation_id ?? `resposta-${response.id}`;

    if (!porConversa.has(chave)) {
      porConversa.set(chave, {
        id: chave,
        contact: response.contact,
        assignedAgent: response.assigned_agent,
        conversationId: response.conversation_id,
        createdAgo: dynamicTime(response.created_at),
        createdAt: messageStamp(response.created_at, 'LLL d yyyy, h:mm a'),
        notas: {},
        // Resposta anterior a 18/09 não tem critério: vai para uma coluna "Nota" à parte, em
        // vez de ser jogada dentro de uma das perguntas atuais.
        semCriterio: [],
        feedbackText: '',
        _original: response,
      });
    }

    const linha = porConversa.get(chave);
    if (response.fandata_criterio) {
      linha.notas[response.fandata_criterio] = response.rating;
    } else {
      linha.semCriterio.push(response.rating);
    }
    // T-209: o clube pede o comentário uma vez só, na última pergunta. Quando existe, é um por
    // atendimento, e o primeiro preenchido é ele.
    if (!linha.feedbackText && response.feedback_message) {
      linha.feedbackText = response.feedback_message;
    }
  });

  return [...porConversa.values()];
});

// Só entram as colunas que a página realmente tem. "Atendimento virtual" e as outras duas são
// mutuamente exclusivas (quem foi atendido só pelo Bira recebe apenas a virtual), então uma
// coluna fixa para cada critério deixaria uma sempre vazia.
const criteriosNaPagina = computed(() =>
  ORDEM_CRITERIOS.filter(criterio =>
    tableData.value.some(linha => linha.notas[criterio] !== undefined)
  )
);

const temSemCriterio = computed(() =>
  tableData.value.some(linha => linha.semCriterio.length)
);

const rotuloCriterio = criterio =>
  t(`SATISFACAO.CRITERIOS.${criterio.toUpperCase()}`);

const getRatingData = rating => {
  return CSAT_RATINGS.find(r => r.value === rating) || {};
};

const columnHelper = createColumnHelper();

const columns = computed(() => {
  const baseColumns = [
    columnHelper.accessor('contact', {
      header: t('CSAT_REPORTS.TABLE.HEADER.CONTACT_NAME'),
    }),
    // T-256: uma coluna por pergunta, na ordem do documento do clube.
    ...criteriosNaPagina.value.map(criterio =>
      columnHelper.accessor(`notas.${criterio}`, {
        header: rotuloCriterio(criterio),
        size: 140,
      })
    ),
    ...(temSemCriterio.value
      ? [
          columnHelper.accessor('semCriterio', {
            header: t('CSAT_REPORTS.TABLE.HEADER.RATING'),
            size: 120,
          }),
        ]
      : []),
    columnHelper.accessor('feedbackText', {
      header: t('CSAT_REPORTS.TABLE.HEADER.FEEDBACK_TEXT'),
      size: 500,
    }),
    columnHelper.accessor('assignedAgent', {
      header: t('CSAT_REPORTS.TABLE.HEADER.HANDLED_BY'),
      size: 160,
    }),
  ];

  if (showExpandableRows.value) {
    baseColumns.push(
      columnHelper.accessor('actions', {
        header: '',
        size: 50,
      })
    );
  }

  return baseColumns;
});

const paginationParams = computed(() => {
  return {
    pageIndex: pageIndex,
    pageSize: 25,
  };
});

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  get columns() {
    return columns.value;
  },
  manualPagination: true,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
  get rowCount() {
    return metrics.value.totalResponseCount;
  },
  state: {
    get pagination() {
      return paginationParams.value;
    },
  },
  onPaginationChange: updater => {
    const newPagination = updater(paginationParams.value);
    emit('pageChange', newPagination.pageIndex);
  },
});
</script>

<template>
  <div
    class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 overflow-hidden"
  >
    <CsatTableLoader v-if="isLoading" />

    <div v-else-if="tableData.length" class="overflow-x-auto">
      <table class="w-full">
        <thead class="bg-n-solid-2 border-b border-n-container">
          <tr>
            <th
              v-for="header in table.getFlatHeaders()"
              :key="header.id"
              :style="{
                width: header.getSize() ? `${header.getSize()}px` : 'auto',
              }"
              class="text-left py-3 px-5 font-medium text-sm text-n-slate-12"
            >
              {{ header.column.columnDef.header }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-container">
          <template v-for="row in tableData" :key="row.id">
            <tr
              class="group hover:bg-n-slate-2 dark:hover:bg-n-solid-3 transition-colors"
              :class="{
                'bg-n-slate-2 dark:bg-n-solid-3': isRowExpanded(row.id),
                'cursor-pointer': showExpandableRows,
              }"
              @click="showExpandableRows && toggleRow(row.id)"
            >
              <td class="py-4 px-5">
                <CsatContactCell
                  :contact="row.contact"
                  :conversation-id="row.conversationId"
                  :created-ago="row.createdAgo"
                  :created-at="row.createdAt"
                />
              </td>
              <!-- T-256: uma célula por pergunta. Atendimento que não recebeu aquela
                   pergunta mostra travessão, e não uma nota emprestada da outra. -->
              <td
                v-for="criterio in criteriosNaPagina"
                :key="criterio"
                class="px-5 py-4"
              >
                <div
                  v-if="row.notas[criterio] !== undefined"
                  class="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg"
                  :style="{
                    backgroundColor: `${getRatingData(row.notas[criterio]).color}20`,
                  }"
                >
                  <span class="text-sm font-medium truncate text-n-slate-12">
                    {{ $t(getRatingData(row.notas[criterio]).translationKey) }}
                  </span>
                </div>
                <span v-else class="text-sm text-n-slate-10">–</span>
              </td>
              <td v-if="temSemCriterio" class="px-5 py-4">
                <div
                  v-for="(nota, i) in row.semCriterio"
                  :key="i"
                  class="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg"
                  :style="{ backgroundColor: `${getRatingData(nota).color}20` }"
                >
                  <span class="text-sm font-medium truncate text-n-slate-12">
                    {{ $t(getRatingData(nota).translationKey) }}
                  </span>
                </div>
                <span v-if="!row.semCriterio.length" class="text-sm text-n-slate-10">
                  –
                </span>
              </td>
              <td class="py-4 px-5">
                <span
                  v-if="!row.feedbackText"
                  class="text-n-slate-10 italic text-sm"
                >
                  {{ $t('CSAT_REPORTS.NO_FEEDBACK') }}
                </span>
                <div v-else class="text-sm text-n-slate-12">
                  <ShowMore :text="row.feedbackText" :limit="100" />
                </div>
              </td>
              <td class="py-4 px-5">
                <UserAvatarWithName
                  v-if="row.assignedAgent"
                  :user="row.assignedAgent"
                  :size="28"
                />
                <span v-else class="text-n-slate-10 text-sm italic">
                  {{ $t('CSAT_REPORTS.NO_AGENT') }}
                </span>
              </td>
              <td v-if="showExpandableRows" class="py-4 px-5">
                <div
                  class="p-1.5 rounded-md text-n-slate-10 group-hover:text-n-slate-12 transition-colors"
                >
                  <i
                    class="size-4 block transition-transform duration-200"
                    :class="
                      isRowExpanded(row.id)
                        ? 'i-lucide-chevron-up'
                        : 'i-lucide-chevron-down'
                    "
                  />
                </div>
              </td>
            </tr>
            <tr
              v-if="showExpandableRows && isRowExpanded(row.id)"
              class="!border-t-0"
            >
              <td :colspan="table.getFlatHeaders().length" class="p-0 !border-t-0">
                <CsatExpandedRow :response="row._original" />
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <CsatEmptyState
      v-else
      :title="$t('CSAT_REPORTS.NO_RECORDS')"
      :description="$t('CSAT_REPORTS.NO_RECORDS_DESCRIPTION')"
    />

    <div
      v-if="metrics.totalResponseCount"
      class="px-6 py-4 border-t border-n-weak"
    >
      <Pagination :table="table" />
    </div>
  </div>
</template>
